import type {CopilotAssignmentRepository} from '@github-ui/agent-sessions/components/copilot-assignment/copilot-assignment-types'
import {createContext, useCallback, useContext, useMemo, useState} from 'react'

import {MemexColumnDataType} from '../../api/columns/contracts/memex-column'
import type {UpdateColumnValueAction} from '../../api/columns/contracts/storage'
import {ItemType} from '../../api/memex-items/item-type'
import type {FieldGrouping} from '../../features/grouping/types'
import {isCopilot} from '../../helpers/assignee-helper'
import type {MemexItemModel} from '../../models/memex-item-model'

type CopilotAssignmentModalState = {
  isOpen: boolean
  repository: CopilotAssignmentRepository | null
  issueNumbers: Array<number>
  onSuccess?: () => Promise<void>
}

export type CopilotModalForIssuesProps = (
  items: Array<MemexItemModel>,
  onSuccess: () => Promise<void>,
  repositoryFilter?: (item: MemexItemModel) => boolean,
) => boolean

type CopilotAssignmentModalContextValue = {
  modalState: CopilotAssignmentModalState
  openModal: (
    repository: CopilotAssignmentRepository,
    issueNumbers: Array<number>,
    onSuccess?: () => Promise<void>,
  ) => void
  closeModal: () => void
  handleAssignmentComplete: () => Promise<void>
  tryOpenCopilotModal: (
    items: Array<MemexItemModel>,
    sourceObject: FieldGrouping,
    onSuccess: () => Promise<void>,
  ) => boolean
  openCopilotModalForIssues: CopilotModalForIssuesProps
}

const CopilotAssignmentModalContext = createContext<CopilotAssignmentModalContextValue | null>(null)

export const CopilotAssignmentModalProvider: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [modalState, setModalState] = useState<CopilotAssignmentModalState>({
    isOpen: false,
    repository: null,
    issueNumbers: [],
  })

  const openModal = useCallback(
    (repository: CopilotAssignmentRepository, issueNumbers: Array<number>, onSuccess?: () => Promise<void>) => {
      setModalState({
        isOpen: true,
        repository,
        issueNumbers,
        onSuccess,
      })
    },
    [],
  )

  const closeModal = useCallback(() => {
    setModalState({
      isOpen: false,
      repository: null,
      issueNumbers: [],
    })
  }, [])

  const handleAssignmentComplete = useCallback(async () => {
    await modalState.onSuccess?.()
    closeModal()
  }, [closeModal, modalState])

  const openCopilotModalForIssues = useCallback(
    (
      items: Array<MemexItemModel>,
      onSuccess: () => Promise<void>,
      repositoryFilter?: (item: MemexItemModel) => boolean,
    ): boolean => {
      // Filter for issues only to get repository info and issue numbers
      const issueItems = items.filter(item => {
        const nameWithOwner = item.getNameWithOwner()
        const issueNumber = item.getItemNumber()
        const isValidIssue =
          item.contentType === ItemType.Issue && nameWithOwner && nameWithOwner.length === 2 && issueNumber > 0

        // Apply additional repository filter if provided
        return isValidIssue && (repositoryFilter ? repositoryFilter(item) : true)
      })

      if (issueItems.length === 0) {
        return false
      }

      // Use the first qualified issue for repository information
      const firstIssue = issueItems[0]
      if (!firstIssue) return false

      const nameWithOwner = firstIssue.getNameWithOwner()
      const [owner, repo] = nameWithOwner as [string, string]

      const repository: CopilotAssignmentRepository = {
        name: repo,
        owner: {
          login: owner,
        },
      }

      openModal(
        repository,
        issueItems.map(item => item.getItemNumber()),
        onSuccess,
      )
      return true
    },
    [openModal],
  )

  const tryOpenCopilotModal = useCallback(
    (items: Array<MemexItemModel>, sourceObject: FieldGrouping, onSuccess: () => Promise<void>): boolean => {
      // Check if we should open the Copilot assignment modal for any of the selected items
      if (!shouldOpenCopilotModal(items, sourceObject)) {
        return false
      }

      // Open the modal
      return openCopilotModalForIssues(items, onSuccess)
    },
    [openCopilotModalForIssues],
  )

  const contextValue: CopilotAssignmentModalContextValue = useMemo(
    () => ({
      handleAssignmentComplete,
      modalState,
      openModal,
      closeModal,
      tryOpenCopilotModal,
      openCopilotModalForIssues,
    }),
    [handleAssignmentComplete, modalState, openModal, closeModal, tryOpenCopilotModal, openCopilotModalForIssues],
  )

  return (
    <CopilotAssignmentModalContext.Provider value={contextValue}>{children}</CopilotAssignmentModalContext.Provider>
  )
}

export function useCopilotAssignmentModal(): CopilotAssignmentModalContextValue {
  const context = useContext(CopilotAssignmentModalContext)

  if (!context) {
    throw new Error('Must use `useCopilotAssignmentModal` in child of `<CopilotAssignmentModalProvider>`')
  }

  return context
}

/**
 * Determines if the Copilot assignment modal should be opened for the given items
 */
export function shouldOpenCopilotModal(
  itemsToMove: Array<MemexItemModel>,
  sourceObject: FieldGrouping | UpdateColumnValueAction,
): boolean {
  // Check if the source object has a Copilot assignee
  const isCopilotAssignment =
    sourceObject.dataType === MemexColumnDataType.Assignees &&
    Array.isArray(sourceObject.value) &&
    sourceObject.value.some(item => isCopilot(item.login))

  if (!isCopilotAssignment) {
    return false
  }

  // Return true if any of the items is an issue
  return itemsToMove.some(item => item.contentType === ItemType.Issue)
}

try{ CopilotAssignmentModalContext.displayName ||= 'CopilotAssignmentModalContext' } catch {}
try{ CopilotAssignmentModalProvider.displayName ||= 'CopilotAssignmentModalProvider' } catch {}